import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { forwardRef } from 'react';
import { css, styled } from 'styled-components';

export const BUTTON_MD_INLINE_PADDING = '1.75rem';

export type ButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: 'a' | 'button';
    'data-test-id'?: string;
    leftIcon?: ReactElement;
    rightIcon?: ReactElement;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  };

export type ButtonContentProps = {
  children: ReactNode;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  topIcon?: ReactElement;
};

const buttonSizeExtraSmall = css`
  height: 2.25rem;
  padding: 0.5rem 1rem;

  line-height: 1.5rem;
  font-size: 0.75rem;
`;

const buttonSizeSmall = css`
  height: 2.625rem;
  padding: 0.75rem 1.25rem;

  line-height: 1.5;
  font-size: 0.875rem;
`;

const buttonSizeNormal = css`
  height: 3.25rem;
  padding: 0.875rem ${BUTTON_MD_INLINE_PADDING};

  line-height: 1.75;
  font-size: 0.875rem;
`;

const buttonSizeLarge = css`
  height: 3.875rem;
  padding: 1rem 2rem;

  line-height: 2.5;
  font-size: 1rem;
`;

const IconWrapperSC = styled.span<{ $iconType: 'left' | 'right' }>`
  display: inline-flex;
  align-self: center;
  flex-shrink: 0;
  margin-inline-end: ${(props) => (props.$iconType === 'left' ? '0.5rem' : 0)};
  margin-inline-start: ${(props) => (props.$iconType === 'right' ? '0.5rem' : 0)};
  word-wrap: break-word;

  > svg {
    width: 1rem;
    height: 1rem;
  }
`;

const TopIconWrapperSC = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const TopIconSC = styled.span`
  padding-block-start: 0.5rem;
  display: flex;

  > svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const TopIconLabelSC = styled.span`
  padding-block-end: 0.2rem;
  display: flex;
`;

export const DefaultButtonSC = styled.button<{ $disabled?: boolean; $size?: 'xs' | 'sm' | 'md' | 'lg' }>`
  ${(props) => {
    switch (props.$size) {
      case 'xs':
        return buttonSizeExtraSmall;
      case 'sm':
        return buttonSizeSmall;
      case 'lg':
        return buttonSizeLarge;
      default:
        return buttonSizeNormal;
    }
  }}

  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  position: relative;

  width: auto;
  font-family: Montserrat, sans-serif;
  font-weight: 500;

  cursor: pointer;
  white-space: nowrap;
  border: 0;
  outline: 0;
  user-select: none;
  text-decoration: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;

  &[disabled],
  &:disabled {
    box-shadow: none;
    cursor: default;
    pointer-events: none;
  }

  &:focus {
    box-shadow: 0 0 0 0.1875rem ${(props) => props.theme.colors.emeraldGreen[50]};
  }
`;

export const ButtonContent = ({ children, leftIcon, rightIcon, topIcon }: ButtonContentProps) => {
  if (topIcon) {
    return (
      <TopIconWrapperSC>
        <TopIconSC>{topIcon}</TopIconSC>
        <TopIconLabelSC>{children}</TopIconLabelSC>
      </TopIconWrapperSC>
    );
  }

  return (
    <>
      {leftIcon && <IconWrapperSC $iconType="left">{leftIcon}</IconWrapperSC>}
      {children}
      {rightIcon && <IconWrapperSC $iconType="right">{rightIcon}</IconWrapperSC>}
    </>
  );
};

/**
 * @property name
 * - has to be provided for analytics to track event "buttonClick".
 * - variant="link" is tracked even without "name" as separate event "linkClick"
 */
export const Button = forwardRef(
  (
    { as = 'button', children, disabled, leftIcon, rightIcon, size = 'md', type = 'button', ...props }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    return (
      <DefaultButtonSC ref={ref} $disabled={disabled} $size={size} as={as} type={type} {...props}>
        <ButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
          {children}
        </ButtonContent>
      </DefaultButtonSC>
    );
  },
);
